import { useCallback, useEffect, useState } from 'react';
import { getContacts, getContact, replyToContact, updateContactStatus } from '../../services/contactService';
import { getErrorMessage } from '../../api/client';
import Modal from './Modal';

const getStatusClass = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (normalized === 'new') return 'pending';
  if (normalized === 'replied') return 'paid';
  if (normalized === 'closed') return 'unknown';
  return 'unknown';
};

const ContactsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [status, setStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedContact, setSelectedContact] = useState(null);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [replyMessage, setReplyMessage] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');
  const [isActionRunning, setIsActionRunning] = useState(false);

  const fetchContacts = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await getContacts({ status, page, limit: pagination.limit });
      setContacts(result.data);
      setPagination({
        page: result.pagination?.page || page,
        limit: result.pagination?.limit || pagination.limit,
        total: result.pagination?.total || 0
      });
    } catch (fetchError) {
      setError(getErrorMessage(fetchError, 'Unable to load contact messages.'));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, pagination.limit]);

  useEffect(() => {
    fetchContacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const totalPages = Math.max(1, Math.ceil(pagination.total / pagination.limit));

  const openDetail = async (id) => {
    setDetailError('');
    setActionMessage('');
    setActionError('');
    setReplyMessage('');
    setIsDetailLoading(true);
    setSelectedContact({ id });
    try {
      const result = await getContact(id);
      setSelectedContact(result.data);
      fetchContacts(pagination.page);
    } catch (fetchError) {
      setDetailError(getErrorMessage(fetchError, 'Unable to load the message.'));
    } finally {
      setIsDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedContact(null);
  };

  const handleReply = async (id) => {
    if (!replyMessage.trim()) {
      setActionError('Please enter a reply message.');
      return;
    }
    setIsActionRunning(true);
    setActionError('');
    setActionMessage('');
    try {
      await replyToContact(id, replyMessage.trim());
      setActionMessage('Reply sent successfully.');
      setReplyMessage('');
      const result = await getContact(id);
      setSelectedContact(result.data);
      fetchContacts(pagination.page);
    } catch (replyError) {
      setActionError(getErrorMessage(replyError, 'Unable to send reply.'));
    } finally {
      setIsActionRunning(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    setIsActionRunning(true);
    setActionError('');
    setActionMessage('');
    try {
      await updateContactStatus(id, newStatus);
      setActionMessage(`Marked as ${newStatus}.`);
      const result = await getContact(id);
      setSelectedContact(result.data);
      fetchContacts(pagination.page);
    } catch (statusError) {
      setActionError(getErrorMessage(statusError, 'Unable to update status.'));
    } finally {
      setIsActionRunning(false);
    }
  };

  return (
    <div>
      <div className="row g-3 align-items-end mb-3">
        <div className="col-6 col-lg-3">
          <label className="form-label text-white fw-bold">Status</label>
          <select className="form-select theme-field px-3" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="new">New</option>
            <option value="replied">Replied</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="col-12 col-lg-2">
          <span className="theme-mini-card admin-panel-count d-flex align-items-center justify-content-center px-2 py-2">
            {pagination.total}
          </span>
        </div>
      </div>

      {isLoading && <div className="theme-state text-center p-3">Loading messages...</div>}
      {!isLoading && error && <div className="theme-state theme-error text-center p-3">{error}</div>}
      {!isLoading && !error && contacts.length === 0 && (
        <div className="theme-state text-center p-3">No messages found.</div>
      )}

      {!isLoading && !error && contacts.length > 0 && (
        <>
          <div className="admin-panel-table-wrap">
            <table className="table table-hover mb-0 admin-panel-table">
              <thead>
                <tr>
                  <th>Sr No.</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((contact, index) => (
                  <tr key={contact.id}>
                    <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.subject}</td>
                    <td>
                      <span className={`theme-status ${getStatusClass(contact.status)}`}>{contact.status}</span>
                    </td>
                    <td>
                      <div className="admin-panel-actions">
                        <button type="button" className="admin-action-btn" onClick={() => openDetail(contact.id)}>
                          View
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="admin-panel-pagination mt-3">
            <button
              type="button"
              className="btn theme-btn theme-btn-outline px-3"
              disabled={pagination.page <= 1}
              onClick={() => fetchContacts(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="text-white">
              Page {pagination.page} of {totalPages}
            </span>
            <button
              type="button"
              className="btn theme-btn theme-btn-outline px-3"
              disabled={pagination.page >= totalPages}
              onClick={() => fetchContacts(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {selectedContact && (
        <Modal title="Contact Message" onClose={closeDetail}>
          {isDetailLoading && <div className="theme-state text-center p-3">Loading...</div>}
          {!isDetailLoading && detailError && <div className="theme-state theme-error text-center p-3">{detailError}</div>}

          {!isDetailLoading && !detailError && selectedContact.name && (
            <div>
              <div className="admin-detail-grid mb-3">
                <div className="admin-detail-item"><span className="label">Name</span><span className="value">{selectedContact.name}</span></div>
                <div className="admin-detail-item"><span className="label">Email</span><span className="value">{selectedContact.email}</span></div>
                <div className="admin-detail-item"><span className="label">Phone</span><span className="value">{selectedContact.phone || '-'}</span></div>
                <div className="admin-detail-item">
                  <span className="label">Status</span>
                  <span className={`theme-status ${getStatusClass(selectedContact.status)}`}>{selectedContact.status}</span>
                </div>
              </div>
              <div className="mb-3">
                <span className="label admin-detail-item">Subject</span>
                <p className="text-white">{selectedContact.subject}</p>
              </div>
              <div className="mb-3">
                <span className="label">Message</span>
                <p className="theme-copy">{selectedContact.message}</p>
              </div>

              {selectedContact.reply_message && (
                <div className="theme-mini-card p-3 mb-3">
                  <span className="label d-block mb-1">Admin Reply</span>
                  <p className="text-white mb-0">{selectedContact.reply_message}</p>
                </div>
              )}

              {actionMessage && <div className="submission-message success">{actionMessage}</div>}
              {actionError && <div className="submission-message error">{actionError}</div>}

              <div className="mb-3">
                <label className="form-label text-white fw-bold">Send Reply</label>
                <textarea
                  className="form-control theme-field px-3"
                  rows="4"
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                />
              </div>

              <div className="admin-panel-actions">
                <button type="button" className="admin-action-btn approve" disabled={isActionRunning} onClick={() => handleReply(selectedContact.id)}>
                  Send Reply
                </button>
                <button
                  type="button"
                  className="admin-action-btn"
                  disabled={isActionRunning || selectedContact.status === 'closed'}
                  onClick={() => handleStatusChange(selectedContact.id, 'closed')}
                >
                  Mark Closed
                </button>
              </div>
            </div>
          )}
        </Modal>
      )}
    </div>
  );
};

export default ContactsTab;
